import { useTranslation } from "react-i18next";
import { StandardTable, StatusBadge } from "@components";
import { statusCancelled, statusDraft, statusPickup } from "@design-system";
import {
  DriverTimeEntry,
  hasAdjustedTime,
  timeEntryDay,
  timeEntryDuration,
  timeEntryRange,
} from "./timeEntries";

/** Read-only presentation: durations remain the server's week-clipped values. */
export function DriverTimeEntriesTable({
  entries,
  overlaps,
  total,
}: {
  entries: DriverTimeEntry[];
  overlaps: Set<string>;
  total: number;
}) {
  const { t, i18n } = useTranslation("driver");
  const columns = [
    "day",
    "time_range",
    "break",
    "duration",
    "comment",
    "source",
  ];
  const cellClass = "px-3 py-3 text-sm align-top";
  const rows = entries.map((entry) => {
    const start = entry.reported?.start || entry.recordedStart;
    const end = entry.reported?.end || entry.recordedEnd;
    const active = !!start && !end && !entry.voided;
    const time = timeEntryRange(start, end);
    const recorded = timeEntryRange(entry.recordedStart, entry.recordedEnd);
    const values = [
      <span className="whitespace-nowrap">
        {timeEntryDay(start, i18n.resolvedLanguage || i18n.language || "no")}
      </span>,
      <div className="space-y-1">
        <span
          className={`whitespace-nowrap ${entry.voided ? "line-through" : ""}`}
        >
          {time}
        </span>
        {hasAdjustedTime(entry) && (
          <p className="text-xs text-muted">
            {t("profile.recorded_time", { time: recorded })}
          </p>
        )}
        {active && <StatusBadge status={t("profile.active")} />}
        {entry.voided && (
          <StatusBadge status={t("profile.voided")} bgStyle={statusCancelled} />
        )}
        {overlaps.has(entry.id) && (
          <p className="text-xs text-warning">{t("profile.overlap")}</p>
        )}
      </div>,
      entry.reported
        ? t("profile.minutes", { count: entry.reported.breakMinutes })
        : "—",
      <span className="font-medium tabular-nums">
        {entry.voided || active ? "—" : timeEntryDuration(entry.periodSeconds)}
      </span>,
      <div className="min-w-[10rem] max-w-lg whitespace-pre-wrap break-words">
        {entry.reported?.comment || (!entry.voided && "—")}
        {entry.voided && entry.voidReason && (
          <p>
            {t("profile.voided")}: {entry.voidReason}
          </p>
        )}
      </div>,
      <StatusBadge
        status={t(
          entry.source === "manual" ? "profile.manual_short" : "profile.app",
        )}
        bgStyle={entry.source === "manual" ? statusDraft : statusPickup}
      />,
    ];
    return {
      id: entry.id,
      cells: columns.map((id, index) => ({
        id,
        children: values[index],
        showOnSmallScreen: true,
        classOverride: `${cellClass} ${id === "duration" ? "text-right" : ""} ${entry.voided ? "text-muted" : "text-text"}`,
      })),
    };
  });
  return (
    <StandardTable
      header={columns.map((id) => ({
        id,
        children: t(`profile.${id}`),
        showOnSmallScreen: true,
        classOverride: `whitespace-nowrap ${id === "duration" ? "text-right" : ""}`,
      }))}
      rows={[
        ...rows,
        {
          id: "week-total",
          cells: columns.map((id) => ({
            id,
            showOnSmallScreen: true,
            classOverride: `${cellClass} text-text font-semibold ${id === "duration" ? "text-right tabular-nums" : ""}`,
            children:
              id === "day"
                ? t("profile.total_short")
                : id === "duration"
                  ? timeEntryDuration(total)
                  : "",
          })),
        },
      ]}
    />
  );
}
